<script lang="ts">
  import { fade } from "svelte/transition";
  import { ServerError } from "@ui/http";
  import Login from "./Login.svelte";
  import Signup from "./Signup.svelte";
  import ForgotPassword from "./ForgotPassword.svelte";
  import ResetPasswordEmailSent from "./ResetPasswordEmailSent.svelte";
  import ResetPassword from "./ResetPassword.svelte";
  import VerifyCodeByEmail from "./VerifyCodeByEmail.svelte";
  import { getAuthenticationContext } from "./context";
  import FireImage from "./images/fire.svg";
  import SmoreLoginLogo from "./images/smore-logo-login.png";
  import PartySmorinio from "./images/party-smorinio.svg";
  import { createEventDispatcher } from "svelte";
  import type { AuthenticationMode, CodeVerification } from "./types";
  import { CLASSIC_DOMAIN_ERROR_CODE, CLASSIC_DOMAIN_MESSAGE } from "./classicDomains";

  const SHOW_LOGIN_BUTTON_MODES: AuthenticationMode[] = ["login", "signup", "accept-tos"];

  const codeToMessage: { [code: string]: string | ((mode: AuthenticationMode) => string) } = {
    "nouser": "Seems like you put in the wrong email or password.",
    "nosuchuser": "Hmm... sorry we can't seem to find this email.",
    "emailuser": "Please try to login with your email address and password below.",
    "tryemail": "Please try to login with your email address and password below.",
    "invalidtoken": "Seems like we can't automatically log you in, but you can still login manually.",
    "nosignupallowed": "Oops! That info doesn’t match any accounts we have.<br/>Visit smore.com from your desktop to sign up.",
    "googleuser": "In order to login with Gmail, please use the Continue with Google button below.",
    "trygoogle": "In order to login with Gmail, please use the Continue with Google button below.",
    "trysaml": "Try logging in with your SAML account.",
    // SMOR-1241. Reached from every refused door: password login, signup, Google, SAML and the two
    // password-reset endpoints all answer with this one code.
    [CLASSIC_DOMAIN_ERROR_CODE]: CLASSIC_DOMAIN_MESSAGE,
    "badpassword": "Seems like you put in the wrong email or password.",
    "confirm_not_a_robot": "Please confirm you are not a robot.",
    "user exists": "We already have a user with that email.<br/>Try logging in instead.",
    "email": "Your email address is invalid",
    "missing_first_name": "First name can't be blank",
    "missing_last_name": "Last name can't be blank",
    "first_name_too_long": "First name is too long",
    "last_name_too_long": "Last name is too long",
    "invalid_first_name": "Invalid first name",
    "invalid_last_name": "Invalid last name",
    "invalid_verification_code": "Code is invalid",
    "expired_verification_code": "Code has expired. Please try again",
    "google_recaptch_error": "Please make sure you've marked yourself as 'Not a Robot'",
    "invite_email": "Use email address from Invite",
    "password_compromised": (mode) => {
      return mode === "login"
        ? `Please <a href="/reset-your-password" class="underline">reset your password</a> to something more strong and secure. <br/><br/> Your password was identified as being too common and easy to guess.`
        : `Your password is too common or easy to guess.<br/>Please create a stronger password.`;
    },
    "password_breaks_policy": `Your password is too common or easy to guess.<br/>Please create a stronger password.`
  };

  export let errorCode: string | undefined = undefined;
  export let email: string = "";
  export let resetPasswordToken: string = "";
  export let codeVerification: CodeVerification | undefined = undefined;
  export let hideSmoreLogo: boolean = false;
  export let disableReCaptcha: boolean = false;
  export let norc: boolean = false;

  const context = getAuthenticationContext();
  const dispatcher = createEventDispatcher();

  const urlParams = new URLSearchParams(window.location.search);
  $: hideReCaptcha = norc || urlParams.has("norc");

  let signupStep: number = 1;

  function toggleLoginSignup() {
    let newMode = "login";
    if ($context.mode === "login") {
      newMode = "signup";
    }
    // Redundant, and kept deliberately. `errcode` is bound two-way from authenticate.svelte, and
    // its `clearStaleMessage()` runs on every route to another screen - this toggle included - so
    // this line no longer does anything the parent would not do a moment later.
    //
    // It stays as belt and braces, not because it is load-bearing: an earlier version of this
    // comment defended it on the grounds that it "demonstrably worked before", which is
    // superstition rather than a reason. If you are tidying up, this is safe to delete; just check
    // the "Create an account" toggle still clears the banner afterwards.
    errorCode = undefined;
    dispatcher("modeChanged", newMode);
  }

  function errMsg(code: string) {
    const m = codeToMessage[code] || ServerError.GENERIC_ERROR;
    if (typeof m === "function") {
      return m($context.mode);
    }
    return m;
  }
</script>

<div class="flex w-full items-start justify-center" class:justify-between={signupStep === 1}>
  {#if signupStep === 1}
    {#if !hideSmoreLogo}
      {#if $context.disableSmoreLogoLink}
        <img class="smore-logo mb-6" src={SmoreLoginLogo} alt="Smore logo" />
      {:else}
        <a href="/app" class="focusable">
          <img class="smore-logo mb-6" src={SmoreLoginLogo} alt="Smore logo" />
        </a>
      {/if}
    {/if}

    {#if $context.hideSignup && $context.backUrl}
      <a
        href={$context.backUrl}
        class="clickable focusable rounded border border-nxgray-200 bg-white px-3 py-2 text-14 font-bold text-primary shadow hover:bg-nxgray-50"
        >« Back</a>
    {:else if !$context.preventSignup && !$context.hideSignup && SHOW_LOGIN_BUTTON_MODES.includes($context.mode)}
      <button
        on:click|preventDefault|stopPropagation={toggleLoginSignup}
        class="clickable focusable rounded border border-nxgray-200 bg-white px-3 py-2 text-14 font-bold text-primary shadow hover:bg-nxgray-50"
        >{$context.mode === "login" ? "Create an account" : "Log in"}</button>
    {/if}
  {:else}
    <img class="w-20 -rotate-6 transform md:w-auto" src={PartySmorinio} alt="Party Smorinio" />
    <div class="ml-4 text-warning-600">
      <h2 class="text-21 font-bold text-warning-600">Thanks for choosing Smore!</h2>
      <div class="text-15">You’re about to create amazing things.</div>
    </div>
  {/if}
</div>
{#if errorCode}
  <!-- role="alert" so the refusal is announced. Pressing Log in and being refused does not navigate
       and moves no focus, so without this a screen-reader user is told nothing at all. Every refusal
       on this screen renders here, so the one attribute covers all of them. -->
  <div transition:fade role="alert" class="mb-4 rounded bg-danger-600 px-4 py-2 font-bold text-white">
    {@html errMsg(errorCode)}
  </div>
{/if}
<div>
  {#if $context.mode === "login"}
    <div>
      <Login on:login on:require2FA bind:errorCode {email} on:modeChanged showReCaptcha={!hideReCaptcha} />
    </div>
  {:else if $context.mode === "signup" || $context.mode === "accept-tos"}
    <div>
      <Signup
        on:signup
        bind:errorCode
        {email}
        on:modeChanged
        showReCaptcha={!hideReCaptcha && !disableReCaptcha}
        showTermsAndContinueWithGoogle={$context.mode === "accept-tos"} />
    </div>
  {:else if $context.mode === "forgot-password"}
    <ForgotPassword {email} bind:errorCode on:modeChanged />
  {:else if $context.mode === "reset-your-password"}
    <ForgotPassword {email} bind:errorCode on:modeChanged compromised={true} />
  {:else if $context.mode === "reset-password-email-sent"}
    <ResetPasswordEmailSent on:modeChanged />
  {:else if $context.mode === "reset-password"}
    <ResetPassword token={resetPasswordToken} />
  {:else if $context.mode === "login-code" && codeVerification}
    <VerifyCodeByEmail {codeVerification} bind:errorCode on:modeChanged on:login />
  {/if}
</div>

<style>
  .smore-logo {
    height: 44px;
  }
  @media (max-width: 732px) {
    .smore-logo {
      height: 30px;
    }
  }
</style>
